import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { useEmailSubmit } from '../../hooks/useEmailSubmit';

const QuoteForm = () => {
  const quoteFormRef = useRef();
  const { isSubmitting, submit } = useEmailSubmit({
    successMessage: 'Quote request sent successfully! Our team will contact you within 24 hours.',
    errorMessage: 'Failed to send quote request. Please try again or contact us directly.',
    sheetName: 'Quote Requests',
  });

  const buildParams = (fd, form) => {
    const category = form.product_category.selectedOptions[0]?.text || fd.get('product_category');
    return {
      emailParams: {
        name: fd.get('user_name'),
        email: fd.get('user_email'),
        phone: '-',
        message: `Request Type: Quote Request
Company: ${fd.get('user_name')}
Product Category: ${category}
Order Quantity: ${fd.get('quantity')}

Project Details:
${fd.get('message')}`,
      },
      sheetRow: {
        Company: fd.get('user_name'),
        Email: fd.get('user_email'),
        'Product Category': category,
        'Order Quantity': fd.get('quantity'),
        'Project Details': fd.get('message'),
      },
    };
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h2
        className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A57] mb-6"
        style={{ fontFamily: 'Cabinet Grotesk' }}
        data-testid="quote-form-title"
      >
        Request a Quote
      </h2>
      <p className="text-base text-[#6B7280] leading-relaxed mb-8">
        Fill out the details below and our team will provide you with a competitive
        quote within 24 hours.
      </p>

      <form
        ref={quoteFormRef}
        onSubmit={(e) => submit(e, quoteFormRef, buildParams)}
        className="space-y-6"
        data-testid="quote-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="quote_name" className="block text-sm font-medium text-[#0B2A57] mb-2">
              Company Name *
            </label>
            <input
              type="text"
              id="quote_name"
              name="user_name"
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Your Company"
              data-testid="quote-company-input"
            />
          </div>

          <div>
            <label htmlFor="quote_email" className="block text-sm font-medium text-[#0B2A57] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="quote_email"
              name="user_email"
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="email@company.com"
              data-testid="quote-email-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="product_category" className="block text-sm font-medium text-[#0B2A57] mb-2">
              Product Category *
            </label>
            <select
              id="product_category"
              name="product_category"
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              data-testid="quote-category-select"
            >
              <option value="">Select Category</option>
              <option value="apparel">Apparel & Fashion</option>
              <option value="tiles">Tiles & Surfaces</option>
              <option value="bathware">Bathware & Sanitaryware</option>
              <option value="home-decor">Home & Decor</option>
              <option value="furniture">Furniture</option>
              <option value="textiles">Textiles & Made-Ups</option>
              <option value="handicrafts">Handicrafts</option>
              <option value="packaging">Packaging</option>
              <option value="electrical">Electrical</option>
              <option value="plumbing">Plumbing Pipes & Fittings</option>
              <option value="chemicals">Chemicals</option>
              <option value="custom-sourcing">Custom Sourcing</option>
            </select>
          </div>

          <div>
            <label htmlFor="quantity" className="block text-sm font-medium text-[#0B2A57] mb-2">
              Order Quantity *
            </label>
            <input
              type="text"
              id="quantity"
              name="quantity"
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="e.g., 5000 pcs"
              data-testid="quote-quantity-input"
            />
          </div>
        </div>

        <div>
          <label htmlFor="quote_message" className="block text-sm font-medium text-[#0B2A57] mb-2">
            Project Details *
          </label>
          <textarea
            id="quote_message"
            name="message"
            required
            disabled={isSubmitting}
            rows={6}
            className="w-full px-4 py-3 rounded-lg border border-[#0B2A57]/20 focus:border-[#1E3D66] focus:ring-2 focus:ring-[#1E3D66]/20 outline-none transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="Please provide details about your product requirement, quantities, specifications and timeline..."
            data-testid="quote-message-input"
          />
        </div>

        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#0B2A57] text-white font-medium transition-all hover:shadow-lg hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          data-testid="quote-submit-button"
        >
          {isSubmitting ? (
            'Sending...'
          ) : (
            <>
              Request Quote
              <Send size={20} />
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default QuoteForm;
